import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Layout from './components/Layout';
import ResponsiveShadow from './components/ResponsiveShadow';
import PageHeader from './components/PageHeader';
import PageContent from './components/PageContent';
import useDefaultModal from './components/hooks/useDefaultModal';
import useReferenceData from './components/hooks/useReferenceData';
import DefaultModal from './components/modals/DefaultModal';
import { API_BASE_URL } from '../config/ApiConfig';
import LoadingModal from './components/modals/LoadingModal';

function formatCurrency(value) {
  return Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function keyOf(item) {
  return `${item.idPrograma}_${item.idConta}`;
}

function parseContaNumLabel(conta) {
  const s = String(conta || '').trim();
  const m = s.match(/^(\d+)/);
  return m ? parseInt(m[1], 10) : Number.MAX_SAFE_INTEGER;
}

export default function ComparativoSresSaldoFnde() {
  const token = localStorage.getItem('token');
  const cnpj = localStorage.getItem('cnpj');
  const ano = localStorage.getItem('ano');
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const { programas, contas } = useReferenceData(token, cnpj);

  useEffect(() => {
    if (!token || !cnpj || !ano) {
      setIsLoading(false);
      showDefaultModal('error', 'Escola, ano ou token não informados.');
      return;
    }

    setIsLoading(true);

    Promise.all([
      axios.get(`${API_BASE_URL.SRES}/search`, {
        params: { idEscola: cnpj, ano },
        headers: { Authorization: `Bearer ${token}` },
      }),
      axios.get(`${API_BASE_URL.SALDO_FNDE}`, {
        params: { idEscola: cnpj, ano },
        headers: { Authorization: `Bearer ${token}` },
      }),
    ])
      .then(([sresResp, fndeResp]) => {
        const sresList = Array.isArray(sresResp.data) ? sresResp.data : [];
        const fndeList = Array.isArray(fndeResp.data) ? fndeResp.data : [];

        const map = new Map();

        sresList.forEach(s => {
          map.set(keyOf(s), {
            idPrograma: s.idPrograma,
            idConta: s.idConta,
            sresCusteio: Number(s.vlrC || 0),
            sresCapital: Number(s.vlrK || 0),
            saldoFnde: 0,
          });
        });

        fndeList.forEach(f => {
          const key = keyOf(f);
          const totalFnde = Number(f.cdb || 0) + Number(f.poupanca || 0) + Number(f.fundos || 0) + Number(f.conta || 0);
          const existing = map.get(key) || {
            idPrograma: f.idPrograma,
            idConta: f.idConta,
            sresCusteio: 0,
            sresCapital: 0,
            saldoFnde: 0,
          };
          existing.saldoFnde = totalFnde;
          map.set(key, existing);
        });

        const merged = Array.from(map.values())
          .map(item => {
            const totalSres = item.sresCusteio + item.sresCapital;
            return {
              ...item,
              totalSres,
              diferenca: item.saldoFnde - totalSres,
            };
          })
          .sort((a, b) => {
            const na = parseContaNumLabel(a.idConta);
            const nb = parseContaNumLabel(b.idConta);
            if (na !== nb) return na - nb;
            return String(a.idConta || '').localeCompare(String(b.idConta || ''), 'pt-BR', { sensitivity: 'base' });
          });

        setRows(merged);
      })
      .catch(() => {
        setRows([]);
        showDefaultModal('error', 'Erro ao gerar comparativo SRES x Saldo FNDE.');
      })
      .finally(() => setIsLoading(false));
  }, [token, cnpj, ano, showDefaultModal]);

  const resumo = useMemo(() => {
    return rows.reduce(
      (acc, row) => {
        acc.sres += row.totalSres;
        acc.fnde += row.saldoFnde;
        acc.diff += row.diferenca;
        return acc;
      },
      { sres: 0, fnde: 0, diff: 0 }
    );
  }, [rows]);

  const resumoPorPrograma = useMemo(() => {
    const map = {};
    (Array.isArray(rows) ? rows : []).forEach(row => {
      const chave = String(row.idPrograma);
      if (!map[chave]) map[chave] = { idPrograma: row.idPrograma, sres: 0, fnde: 0 };
      map[chave].sres += Number(row.totalSres || 0);
      map[chave].fnde += Number(row.saldoFnde || 0);
    });
    return Object.values(map);
  }, [rows]);

  const programaLabel = (idPrograma) => {
    const p = (programas || []).find(x => String(x.id) === String(idPrograma));
    return p ? p.descricao : idPrograma;
  };

  const contaLabel = (idConta) => {
    const c = (contas || []).find(x => String(x.numConta) === String(idConta) || String(x.idConta) === String(idConta));
    return c ? (c.nomeConta || c.numConta || c.idConta) : idConta;
  };

  return (
    <Layout>
      <LoadingModal show={isLoading} />
      <ResponsiveShadow>
        <PageHeader title="Comparativo SRES x Saldo FNDE" icon="bi-bar-chart-line" />
        <PageContent>
          {resumoPorPrograma.length > 0 && (
            <div className="mb-2 d-flex flex-wrap gap-2" style={{ maxHeight: '30vh', overflowY: 'auto', overflowX: 'hidden', flexShrink: 0 }}>
              {resumoPorPrograma.map(prog => (
                <div className="card border-0 shadow-sm" key={prog.idPrograma} style={{ borderRadius: 10, flex: '1 1 0', minWidth: 200, maxWidth: '25%' }}>
                  <div className="card-body pt-2 pb-1 px-3">
                    <div className="d-flex justify-content-between align-items-center gap-2" style={{ backgroundColor: '#198754', color: '#fff', margin: '-0.5rem -1rem 0.5rem', padding: '0.15rem 1rem', borderRadius: '10px 10px 0 0', minHeight: '22px' }}>
                      <span className="fw-bold text-truncate" style={{ fontSize: '.62rem' }} title={prog.idPrograma}>{programaLabel(prog.idPrograma)}</span>
                    </div>
                    <div className="d-flex justify-content-between" style={{ fontSize: '.72rem', whiteSpace: 'nowrap' }}>
                      <span className="text-muted">SRES</span><span>{formatCurrency(prog.sres)}</span>
                    </div>
                    <div className="d-flex justify-content-between" style={{ fontSize: '.72rem', whiteSpace: 'nowrap' }}>
                      <span className="text-muted">Saldo FNDE</span><span className="text-success">{formatCurrency(prog.fnde)}</span>
                    </div>
                    <div className="d-flex justify-content-between pt-1 mt-1 border-top fw-bold" style={{ fontSize: '.78rem', whiteSpace: 'nowrap' }}>
                      <span>Diferença</span><span className={prog.fnde - prog.sres < 0 ? 'text-danger' : 'text-warning'}>{formatCurrency(prog.fnde - prog.sres)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="d-flex gap-2 flex-wrap mb-3">
            <span className="badge bg-primary">Total SRES: {formatCurrency(resumo.sres)}</span>
            <span className="badge bg-success">Total Saldo FNDE: {formatCurrency(resumo.fnde)}</span>
            <span className={`badge ${resumo.diff < 0 ? 'bg-danger' : 'bg-warning text-dark'}`}>
              Diferença: {formatCurrency(resumo.diff)}
            </span>
          </div>

          {isLoading ? (
            <div className="text-center py-3">
              <span className="spinner-border spinner-border-sm"></span>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover table-sm" style={{ fontSize: '0.9rem' }}>
                <thead className="table-dark">
                  <tr>
                    <th>#</th>
                    <th>Programa</th>
                    <th>Conta</th>
                    <th className="text-end">Saldo (Contas + Investimentos)</th>
                    <th className="text-end">Saldo FNDE</th>
                    <th className="text-end">Diferença</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, idx) => (
                    <tr key={`${row.idPrograma}_${row.idConta}`}>
                      <td>{idx + 1}</td>
                      <td>{programaLabel(row.idPrograma)}</td>
                      <td>{contaLabel(row.idConta)}</td>
                      <td className="text-end fw-bold">{formatCurrency(row.totalSres)}</td>
                      <td className="text-end fw-bold text-success">{formatCurrency(row.saldoFnde)}</td>
                      <td className={`text-end fw-bold ${row.diferenca < 0 ? 'text-danger' : 'text-warning'}`}>
                        {formatCurrency(row.diferenca)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </PageContent>
      </ResponsiveShadow>
      <DefaultModal {...setDefaultModal} />
    </Layout>
  );
}
